import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import SuccessToast from '../SuccessToast';
import ConfirmDialog from '../ConfirmDialog';
import { getApiUrl, apiRequest } from '../../config/api';

function ScriptsPanel({ botId, botName, onBotsNeedReload }) {
  const [toastExito, setToastExito] = useState(null);
  const [toastError, setToastError] = useState(null);
  const [pendienteBorrar, setPendienteBorrar] = useState(null);
  const [scriptContent, setScriptContent] = useState('');
  const [scriptValidation, setScriptValidation] = useState(null);
  const [scriptSaving, setScriptSaving] = useState(false);
  const [scriptStats, setScriptStats] = useState(null);
  const [scriptLogs, setScriptLogs] = useState(null);
  const [scriptExpanded, setScriptExpanded] = useState(false);

  const mostrarExito = (mensaje) => {
    setToastError(null);
    setToastExito(mensaje);
  };

  const mostrarError = (mensaje) => {
    setToastExito(null);
    setToastError(mensaje);
  };

  // ===== FUNCIONES DE GESTIÓN DE SCRIPTS DE BOTS =====

  const loadScript = async (id) => {
    if (!id) return;
    setScriptValidation(null);
    setScriptSaving(false);
    setScriptStats(null);
    setScriptLogs(null);

    try {
      const [scriptRes, statsRes, logsRes] = await Promise.all([
        apiRequest(getApiUrl(`/bots/${id}/script`)),
        apiRequest(getApiUrl(`/bots/${id}/script/stats`)),
        apiRequest(getApiUrl(`/bots/${id}/script/logs`))
      ]);
      if (scriptRes.ok) {
        const data = await scriptRes.json();
        setScriptContent(data.script || '');
      } else {
        setScriptContent('');
      }
      if (statsRes.ok) {
        setScriptStats(await statsRes.json());
      }
      if (logsRes.ok) {
        const logsData = await logsRes.json();
        setScriptLogs(logsData.executions || []);
      }
    } catch (error) {
      console.error('Error loading bot script:', error);
      setScriptContent('');
    }
  };

  useEffect(() => {
    loadScript(botId);
    setScriptExpanded(false);
  }, [botId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!scriptExpanded) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setScriptExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [scriptExpanded]);

  const handleValidateScript = async () => {
    if (!botId) return;
    setScriptValidation(null);
    
    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/script/validate`), {
        method: 'POST',
        body: JSON.stringify({ script: scriptContent })
      });
      const data = await response.json();
      setScriptValidation(data);
    } catch (error) {
      setScriptValidation({ valid: false, error: 'Error de conexión' });
    }
  };

  const handleSaveScript = async () => {
    if (!botId) return;
    setScriptSaving(true);
    setScriptValidation(null);

    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/script`), {
        method: 'PUT',
        body: JSON.stringify({ script: scriptContent })
      });
      const data = await response.json();
      
      if (response.ok) {
        mostrarExito(`Script del bot "${botName}" guardado correctamente`);
        onBotsNeedReload();
      } else {
        setScriptValidation({ valid: false, error: data.details || data.error || 'Error al guardar' });
      }
    } catch (error) {
      setScriptValidation({ valid: false, error: 'Error de conexión' });
    } finally {
      setScriptSaving(false);
    }
  };

  const handleDeleteScript = async () => {
    if (!botId) return;
    setPendienteBorrar(null);

    setScriptSaving(true);

    try {
      const response = await apiRequest(getApiUrl(`/bots/${botId}/script`), {
        method: 'DELETE'
      });
      
      if (response.ok) {
        mostrarExito(`Script del bot "${botName}" eliminado`);
        setScriptContent('');
        onBotsNeedReload();
      } else {
        const data = await response.json();
        mostrarError(data.error || 'Error al eliminar script');
      }
    } catch (error) {
      mostrarError('Error de conexión');
    } finally {
      setScriptSaving(false);
    }
  };

  if (!botId) return null;

  const editor = (
    <div
      className={`script-editor-inline${scriptExpanded ? ' is-expanded' : ''}`}
      role={scriptExpanded ? 'dialog' : undefined}
      aria-modal={scriptExpanded ? 'true' : undefined}
      aria-label={scriptExpanded ? 'Editor de script' : undefined}
    >
      <SuccessToast message={toastExito} onDismiss={() => setToastExito(null)} />
      <SuccessToast message={toastError} variant="error" onDismiss={() => setToastError(null)} />
      {pendienteBorrar && (
        <ConfirmDialog
          title="Eliminar script"
          note="El bot dejará de responder automáticamente."
          onCancel={() => setPendienteBorrar(null)}
          onConfirm={handleDeleteScript}
        >
          ¿Eliminar el script del bot <strong>"{botName}"</strong>?
        </ConfirmDialog>
      )}

      <div className="script-editor-body">
                <div className="script-help">
                  <details>
                    <summary>Ayuda: Cómo escribir un script</summary>
                    <div className="script-help-content">
                      <p>Define una función <code>handleMessage(ctx)</code> que reciba el contexto y devuelva un array de acciones.</p>

                      <p><strong>Contexto disponible (ctx):</strong></p>
                      <table style={{fontSize: '12px', width: '100%', borderCollapse: 'collapse', marginBottom: '10px'}}>
                        <thead><tr style={{background: '#f0f0f0'}}><th style={{padding: '4px 8px', textAlign: 'left', border: '1px solid #ddd'}}>Campo</th><th style={{padding: '4px 8px', textAlign: 'left', border: '1px solid #ddd'}}>Tipo</th><th style={{padding: '4px 8px', textAlign: 'left', border: '1px solid #ddd'}}>Descripción</th></tr></thead>
                        <tbody>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.messageText</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>Texto del mensaje recibido</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.messageType</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>Tipo: "text", "image", "interactive"...</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.from</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>A quién responder: el teléfono, o el BSUID si el usuario oculta su número</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.phone</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string|null</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>WhatsApp: teléfono del usuario aunque oculte su número; null si nunca se conoció</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.contactName</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>Nombre del contacto</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.isNewChat</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>boolean</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>true si es la primera vez que escribe</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.cliente</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>boolean</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>true si un operador encendió el interruptor de Contactos para este chat (WhatsApp e Instagram)</td></tr>
                          <tr style={{background: '#fff8e1'}}><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.buttonId</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string|null</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>ID del botón que pulsó el usuario</td></tr>
                          <tr style={{background: '#fff8e1'}}><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.listId</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>string|null</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>ID de la opción de lista elegida</td></tr>
                          <tr style={{background: '#e8f5e9'}}><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.state</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>object</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>Estado persistente de esta conversación</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.lastBotMessage</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>object|null</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>Último mensaje del bot</td></tr>
                          <tr><td style={{padding: '4px 8px', border: '1px solid #ddd'}}><code>ctx.recentMessages</code></td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>array</td><td style={{padding: '4px 8px', border: '1px solid #ddd'}}>Últimos 10 mensajes del chat</td></tr>
                        </tbody>
                      </table>

                      <p><strong>Acciones disponibles:</strong></p>

                      <details style={{marginBottom: '8px'}}>
                        <summary><code>send_text</code> — Enviar mensaje de texto</summary>
                        <pre style={{margin: '5px 0'}}>{`{ type: "send_text", text: "¡Hola! ¿En qué puedo ayudarte?" }

// text: max 4096 caracteres`}</pre>
                      </details>

                      <details style={{marginBottom: '8px'}}>
                        <summary><code>send_buttons</code> — Enviar mensaje con botones interactivos</summary>
                        <pre style={{margin: '5px 0'}}>{`{
  type: "send_buttons",
  body: "Elige una opción:",        // obligatorio, max 1024 chars
  buttons: [                         // obligatorio, max 3 botones
    { id: "btn_precio", title: "Ver precios" },
    { id: "btn_soporte", title: "Soporte" },
    { id: "btn_info", title: "Más info" }
  ],
  header: "Menú principal",          // opcional, max 60 chars
  footer: "Responde con un botón"    // opcional, max 60 chars
}

// Cuando el usuario pulse un botón, ctx.buttonId = "btn_precio"
// title: max 20 caracteres por botón`}</pre>
                      </details>

                      <details style={{marginBottom: '8px'}}>
                        <summary><code>send_list</code> — Enviar menú de lista desplegable</summary>
                        <pre style={{margin: '5px 0'}}>{`{
  type: "send_list",
  body: "Selecciona un producto:",   // obligatorio, max 1024 chars
  buttonText: "Ver opciones",        // obligatorio, max 20 chars
  sections: [{                       // obligatorio, max 10 secciones
    title: "Planes",
    rows: [                          // max 10 filas por sección
      { id: "plan_basic", title: "Básico - 10€", description: "Ideal para empezar" },
      { id: "plan_pro", title: "Pro - 25€", description: "Para profesionales" }
    ]
  }],
  header: "Nuestros planes",        // opcional, max 60 chars
  footer: "Precios sin IVA"         // opcional, max 60 chars
}

// Cuando el usuario elija una opción, ctx.listId = "plan_basic"
// title de row: max 24 chars, description: max 72 chars`}</pre>
                      </details>

                      <details style={{marginBottom: '8px'}}>
                        <summary><code>set_state</code> — Guardar estado de la conversación</summary>
                        <pre style={{margin: '5px 0'}}>{`{ type: "set_state", state: { step: "menu", intentos: 0 } }

// Persistente por chat (cada usuario tiene su propio state)
// Se lee en la siguiente ejecución como ctx.state
// Max 16KB de datos`}</pre>
                      </details>

                      <hr style={{margin: '10px 0'}}/>
                      <p><strong>Ejemplo completo: Flujo con botones</strong></p>
                      <pre>{`function handleMessage(ctx) {
  var step = (ctx.state && ctx.state.step) || "inicio";

  if (step === "inicio" || ctx.messageText === "menu") {
    return [
      { type: "send_buttons",
        body: "¡Hola " + (ctx.contactName || "") + "! ¿Qué necesitas?",
        buttons: [
          { id: "precios", title: "Ver precios" },
          { id: "soporte", title: "Soporte técnico" },
          { id: "horario", title: "Horarios" }
        ]},
      { type: "set_state", state: { step: "menu" } }
    ];
  }

  if (step === "menu") {
    if (ctx.buttonId === "precios") {
      return [
        { type: "send_list",
          body: "Estos son nuestros planes:",
          buttonText: "Ver planes",
          sections: [{
            title: "Planes disponibles",
            rows: [
              { id: "basic", title: "Básico - 10€/mes",
                description: "1 bot, 1000 msgs" },
              { id: "pro", title: "Pro - 25€/mes",
                description: "5 bots, 10000 msgs" }
            ]
          }]},
        { type: "set_state", state: { step: "planes" } }
      ];
    }
    if (ctx.buttonId === "soporte") {
      return [
        { type: "send_text",
          text: "Un agente te atenderá pronto." },
        { type: "set_state", state: { step: "inicio" } }
      ];
    }
    if (ctx.buttonId === "horario") {
      return [
        { type: "send_text",
          text: "Lunes a Viernes: 9:00 - 18:00" },
        { type: "set_state", state: { step: "inicio" } }
      ];
    }
  }

  if (step === "planes" && ctx.listId) {
    return [
      { type: "send_text",
        text: "Has elegido el plan: " + ctx.listId +
              ". Te contactaremos para activarlo." },
      { type: "set_state", state: { step: "inicio" } }
    ];
  }

  // Fallback: volver al menú
  return [
    { type: "send_text",
      text: "Escribe 'menu' para ver las opciones." }
  ];
}`}</pre>

                      <hr style={{margin: '10px 0'}}/>
                      <p><strong>Bridges</strong></p>
                      <p>Los bridges devuelven datos de JavaScript: no hace falta <code>JSON.parse</code>.</p>

                      <p><strong>HTTP</strong> — <code>get</code>, <code>post</code>, <code>patch</code> y <code>delete</code> devuelven <code>{'{ status, headers, body }'}</code>. <code>status</code> es el código. <code>headers</code> usa nombres en minúsculas. <code>body</code> viene parseado si la respuesta es JSON; si no, es un string, o <code>null</code> si el cuerpo va vacío. En <code>post</code> y <code>patch</code>, un objeto o un array se envía como JSON y un string tal cual.</p>
                      <pre>{`var r = http.get("https://api.example.com/data");
var status = r.status;
var data = r.body;

var creado = http.post("https://api.example.com/send", { key: "value" });`}</pre>

                      <p><strong>db</strong> — <code>save</code> recibe un objeto o un array. <code>find</code>, <code>findByUser</code>, <code>findByPhone</code> y <code>findWhere</code> devuelven un array de registros con <code>data</code> ya parseado.</p>
                      <pre>{`db.save("c", { a: 1 });
var valor = db.find("c")[0].data.a;
var delDia = db.findWhere("c", { fecha: "2026-09-30", n: 3 });`}</pre>

                      <p><strong>gemini</strong> — sin esquema devuelve un string. Con esquema devuelve el objeto ya validado.</p>
                      <pre>{`var texto = gemini.generate("responde corto", null, "gemini-2.5-flash");

var esquema = {
  type: "object",
  properties: { tipo: { type: "string" } },
  required: ["tipo"]
};
var tipo = gemini.generate("clasifica: hola", esquema, "gemini-2.5-flash").tipo;`}</pre>

                      <p><strong>calendar</strong> — <code>freeBusy</code>, <code>listEvents</code>, <code>getEvent</code>, <code>createEvent</code> y <code>updateEvent</code> devuelven un array o un objeto de JavaScript, sin <code>JSON.parse</code>. <code>deleteEvent</code> no devuelve nada. Las fechas son un string ISO-8601 o un <code>Date</code>. El calendario tiene que estar compartido con la cuenta de servicio y dado de alta en el panel Google del bot.</p>
                      <pre>{`var huecos = calendar.freeBusy("usuario@gmail.com", "2026-10-01T08:00:00Z", "2026-10-01T21:00:00Z");
var inicio = huecos[0].start;

var eventos = calendar.listEvents("usuario@gmail.com", {
  timeMin: "2026-10-01T08:00:00Z",
  timeMax: "2026-10-01T21:00:00Z",
  maxResults: 10
});
var titulo = eventos[0].summary;`}</pre>

                      <p><strong>sheets</strong> — <code>read</code> devuelve un array de filas. <code>append</code>, <code>update</code> y <code>clear</code> devuelven un objeto. La hoja tiene que estar compartida con la cuenta de servicio y dada de alta en el panel Google del bot.</p>
                      <pre>{`var filas = sheets.read("1AbCdEfGhIjKlMnOpQr", "Hoja1!A2:D10");
var telefono = filas[0][0];

var escrito = sheets.append("1AbCdEfGhIjKlMnOpQr", "Hoja1!A:B", [
  ["34600000000", "Ana"]
]);
var rango = escrito.updatedRange;`}</pre>

                      <p><strong>vars</strong> — variables que guardan JSON entre mensajes sin pisarse, aunque lleguen dos mensajes a la vez. Hay tres ámbitos: <code>vars.context</code> es del chat y no caduca nunca; <code>vars.cache</code> es del chat y caduca a las 24 horas, o a los segundos que digas en el <code>ttl</code>; <code>vars.botCache</code> la comparten todos los chats del bot y caduca igual. Las de cada chat se ven en Contactos, en la ficha de cada persona, y los administradores también las editan y borran ahí; las del bot, en Configuración.</p>
                      <pre>{`var visitas = vars.context.incr("visitas");           // 1, 2, 3...
var nombre = vars.context.get("nombre", "amigo");     // con valor por defecto
vars.context.set("nombre", "Ana");

vars.cache.push("ultimos", ctx.messageText || "(sin texto)", 5); // se queda con los 5 últimos
vars.cache.set("codigo", "1234", 600);                // caduca en 10 minutos

var total = vars.botCache.incr("mensajes_bot");       // contador de todo el bot

try {
  vars.cache.set("dato", ctx.messageText || null);
} catch (e) {
  console.log(e.message);                             // empieza por "vars:"
}`}</pre>
                      <p>También existen <code>has</code>, <code>keys</code>, <code>getAll</code>, <code>setIfAbsent</code>, <code>delete</code>, <code>deletePrefix</code>, <code>clear</code>, <code>pop</code>, <code>shift</code>, <code>merge</code>, <code>ttl</code> y <code>expire</code>. Para leer, decidir y escribir sin que otro mensaje se cuele en medio: <code>getVersioned</code> y <code>setIfVersion</code>, o <code>atomic</code> con hasta 20 operaciones que se aplican todas o ninguna. Un error lanza una excepción capturable cuyo mensaje empieza por <code>vars:</code>. Guardar <code>undefined</code> lanza <code>vars: valor no serializable</code>: usa <code>null</code> o un valor por defecto, porque un sticker o una imagen llegan sin <code>messageText</code>. Para un contador compartido usa <code>incr</code> y no <code>get</code> seguido de <code>set</code>; lo que es de un usuario, guárdalo en su chat y no en <code>botCache</code>.</p>

                      <p><strong>Límites:</strong> Max 5 acciones por ejecución, 3s timeout, 64KB script, 16KB state; 200 operaciones de vars por ejecución, 16.384 caracteres por variable, 200 variables por chat y 1.000 por bot</p>
                    </div>
                  </details>
                </div>
                
                {scriptStats && scriptStats.totalExecutions > 0 && (
                  <div className="script-stats-panel">
                    <div className="script-stats-grid" data-testid="script-stats-grid">
                      <div className="script-stat">
                        <span className="script-stat-value">{scriptStats.totalExecutions}</span>
                        <span className="script-stat-label">Ejecuciones</span>
                      </div>
                      <div className="script-stat">
                        <span className="script-stat-value">{scriptStats.avgExecutionTimeMs}ms</span>
                        <span className="script-stat-label">Tiempo medio</span>
                      </div>
                      <div className="script-stat">
                        <span className="script-stat-value" style={{color: scriptStats.errorsLast24h > 0 ? '#dc2626' : '#16a34a'}}>
                          {scriptStats.errorsLast24h}
                        </span>
                        <span className="script-stat-label">Errores (24h)</span>
                      </div>
                      <div className="script-stat">
                        <span className="script-stat-value">{scriptStats.errorRate}%</span>
                        <span className="script-stat-label">Tasa error</span>
                      </div>
                    </div>
                    {scriptStats.recentErrors && scriptStats.recentErrors.length > 0 && (
                      <details className="script-errors-detail">
                        <summary>Últimos errores ({scriptStats.recentErrors.length})</summary>
                        <div className="script-errors-list">
                          {scriptStats.recentErrors.map((err, i) => (
                            <div key={i} className="script-error-item">
                              <span className="script-error-type">{err.alertType}</span>
                              <span className="script-error-time">{new Date(err.createdAt).toLocaleString()}</span>
                              <span className="script-error-detail">{err.alertDetail}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                )}

                {scriptLogs && scriptLogs.length > 0 && (
                  <details className="script-logs-detail" style={{marginBottom: '10px'}}>
                    <summary style={{cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', padding: '6px 0'}}>
                      console.log — Últimas {scriptLogs.length} ejecuciones con logs
                    </summary>
                    <div style={{maxHeight: '300px', overflowY: 'auto', background: '#1a1a2e', borderRadius: '6px', padding: '10px', fontFamily: 'monospace', fontSize: '12px'}}>
                      {scriptLogs.map((exec, i) => {
                        const date = new Date(exec.createdAt);
                        const timeStr = date.toLocaleString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'});
                        return (
                          <div key={i} style={{marginBottom: '8px', borderBottom: '1px solid #333', paddingBottom: '8px'}}>
                            <div style={{color: '#888', fontSize: '11px', marginBottom: '3px'}}>
                              {timeStr} — {exec.executionTimeMs}ms — {exec.actionCount} acciones
                              {exec.alertType && <span style={{color: '#ef4444', marginLeft: '8px'}}>[{exec.alertType}]</span>}
                              {exec.messageFrom && <span style={{color: '#666', marginLeft: '8px'}}>from: {exec.messageFrom}</span>}
                            </div>
                            {exec.logs.split('\n').map((line, j) => (
                              <div key={j} style={{color: '#4ade80', paddingLeft: '8px'}}>
                                <span style={{color: '#666'}}>{'>'} </span>{line}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </details>
                )}

                <textarea
                  className="script-textarea"
                  value={scriptContent}
                  onChange={e => {
                    setScriptContent(e.target.value);
                    setScriptValidation(null);
                  }}
                  placeholder={`function handleMessage(ctx) {\n  var actions = [];\n\n  // Tu lógica aquí...\n\n  return actions;\n}`}
                  spellCheck={false}
                />
                
                {scriptValidation && (
                  <div className={`script-validation ${scriptValidation.valid ? 'valid' : 'invalid'}`}>
                    {scriptValidation.valid 
                      ? 'Script válido' 
                      : `Error: ${scriptValidation.error}`
                    }
                  </div>
                )}
                
                <div className="script-actions">
                  <div className="script-actions-left">
                    <button
                      type="button"
                      className="admin-btn secondary"
                      onClick={() => setScriptExpanded((abierto) => !abierto)}
                    >
                      {scriptExpanded ? 'Reducir' : 'Ampliar'}
                    </button>
                    <button
                      className="admin-btn secondary"
                      onClick={handleValidateScript}
                      disabled={!scriptContent.trim() || scriptSaving}
                    >
                      Validar
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {scriptContent.trim() && (
                      <button 
                        className="admin-btn"
                        style={{ background: '#fee2e2', color: '#dc2626' }}
                        onClick={() => setPendienteBorrar(true)}
                        disabled={scriptSaving}
                      >
                        Eliminar
                      </button>
                    )}
                    <button 
                      className="admin-btn primary"
                      onClick={handleSaveScript}
                      disabled={!scriptContent.trim() || scriptSaving}
                    >
                      {scriptSaving ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                </div>
              </div>
    </div>
  );

  return scriptExpanded ? createPortal(editor, document.body) : editor;
}

export default ScriptsPanel;
